// Local persistence (IndexedDB):
//  - 'books'   : downloaded book blobs (fallback when no folder is chosen)
//  - 'handles' : the chosen library-folder handle (File System Access API)
//  - 'filemap' : bookId -> filename, so a book saved into the folder can be
//                found again later

const DB_NAME = 'scrollwind';
const DB_VERSION = 3;

interface StoredBook {
  id: string;
  title: string;
  type: 'PDF' | 'EPUB';
  blob: Blob;
  size: number;
  savedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('books')) db.createObjectStore('books', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('handles')) db.createObjectStore('handles');
      if (!db.objectStoreNames.contains('filemap')) db.createObjectStore('filemap', { keyPath: 'bookId' });
      if (!db.objectStoreNames.contains('covers')) db.createObjectStore('covers', { keyPath: 'bookId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const os = db.transaction(store, mode).objectStore(store);
        const req = run(os);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---- downloaded blobs (IndexedDB fallback) ----

export async function saveBook(id: string, title: string, type: 'PDF' | 'EPUB', blob: Blob): Promise<void> {
  const record: StoredBook = { id, title, type, blob, size: blob.size, savedAt: Date.now() };
  await tx('books', 'readwrite', (s) => s.put(record));
}

export async function getBook(id: string): Promise<StoredBook | undefined> {
  return tx<StoredBook | undefined>('books', 'readonly', (s) => s.get(id) as IDBRequest<StoredBook | undefined>);
}

export async function deleteBook(id: string): Promise<void> {
  await tx('books', 'readwrite', (s) => s.delete(id));
}

export async function storedBookIds(): Promise<Set<string>> {
  const keys = await tx<IDBValidKey[]>('books', 'readonly', (s) => s.getAllKeys());
  return new Set(keys.map(String));
}

// ---- library-folder handle ----

const FOLDER_KEY = 'libraryFolder';

export async function saveFolderHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  await tx('handles', 'readwrite', (s) => s.put(handle, FOLDER_KEY));
}

export async function loadFolderHandle(): Promise<FileSystemDirectoryHandle | undefined> {
  return tx<FileSystemDirectoryHandle | undefined>('handles', 'readonly', (s) =>
    s.get(FOLDER_KEY) as IDBRequest<FileSystemDirectoryHandle | undefined>,
  );
}

export async function clearFolderHandle(): Promise<void> {
  await tx('handles', 'readwrite', (s) => s.delete(FOLDER_KEY));
}

// ---- bookId <-> filename map (books saved into the folder) ----

interface FileMapRow {
  bookId: string;
  name: string;
}

export async function putFileMap(bookId: string, name: string): Promise<void> {
  await tx('filemap', 'readwrite', (s) => s.put({ bookId, name } as FileMapRow));
}

export async function getFileMap(bookId: string): Promise<string | undefined> {
  const row = await tx<FileMapRow | undefined>('filemap', 'readonly', (s) =>
    s.get(bookId) as IDBRequest<FileMapRow | undefined>,
  );
  return row?.name;
}

// ---- generated cover thumbnails (data URLs), cached so we don't re-render ----

interface CoverRow {
  bookId: string;
  // data URL, or '' to remember "this book has no cover" and skip regenerating.
  dataUrl: string;
  at: number;
}

export async function getCoverRec(bookId: string): Promise<string | undefined> {
  const row = await tx<CoverRow | undefined>('covers', 'readonly', (s) =>
    s.get(bookId) as IDBRequest<CoverRow | undefined>,
  );
  return row?.dataUrl;
}

export async function putCoverRec(bookId: string, dataUrl: string): Promise<void> {
  await tx('covers', 'readwrite', (s) => s.put({ bookId, dataUrl, at: Date.now() } as CoverRow));
}

/** Trigger a browser "save file" for a blob. */
export function saveBlobToDisk(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
